import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, Descriptions, Form, Table, type FormInstance } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { LoadProblem } from "./LoadProblem";

interface PreviewData {
  message?: string;
  ok?: boolean;
  columns: { key: string; label: string; align?: "left" | "right" }[];
  rows?: (Record<string, string | number | null> & { key?: string; tone?: string | null })[];
  summary?: { label: string; value: string }[];
  /** UXW-68 (31.10.2026): «Что изменится после сохранения» / «Сохранить не получится» over `lines`, one per point. */
  title?: string;
  lines?: string[];
  /** What the row runs into: shown apart, the card can still be saved. */
  warnings?: string[];
  /** D304: the slots and the tasks that keep a gate on the dock — only the ones the user may open. */
  links?: { label: string; path: string }[];
}

interface Props {
  entity: string;
  id?: string;
  form: FormInstance;
}

/**
 * 3D docks (29.10.2026): what «Сохранить» would do, from the values typed so far — the wall, the row of gates, which
 * places are linked and which are created. The server answers POST /{entity}/form-preview and writes nothing.
 */
export function FormPreview({ entity, id, form }: Props) {
  const values = Form.useWatch((all) => all, form) as Record<string, unknown> | undefined;
  const [asked, setAsked] = useState("");
  // a pause in typing asks once, not on every key
  useEffect(() => {
    const timer = setTimeout(() => setAsked(JSON.stringify(values ?? {})), 400);
    return () => clearTimeout(timer);
  }, [values]);
  const preview = useQuery({
    queryKey: [entity, "form-preview", id ?? "new", asked],
    queryFn: () => api.post<PreviewData>(`/${entity}/form-preview`, { ...(JSON.parse(asked) as object), ...(id ? { id } : {}) }),
    enabled: asked !== "",
    placeholderData: keepPreviousData,
  });
  if (preview.isError) return <LoadProblem error={preview.error} onRetry={() => preview.refetch()} />;
  const data = preview.data;
  if (!data) return null;
  return (
    <div data-testid="form-preview" style={{ marginBottom: 16 }}>
      {data.lines?.length ? (
        <Alert
          type={data.ok === false ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="form-preview-lines"
          message={data.title ?? data.message}
          description={
            <>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {data.links?.length ? (
                <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 16px" }} data-testid="form-preview-links">
                  {data.links.map((l) => (
                    <Link key={l.path} to={l.path}>
                      {l.label} →
                    </Link>
                  ))}
                </div>
              ) : null}
            </>
          }
        />
      ) : (
        data.message && <Alert type={data.ok === false ? "warning" : "info"} showIcon style={{ marginBottom: 12 }} message={data.message} />
      )}
      {data.warnings?.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="form-preview-warnings"
          message="Проверьте перед сохранением"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {data.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      {data.summary?.length ? (
        <Descriptions size="small" column={{ xs: 1, sm: 3 }} bordered style={{ marginBottom: 12 }}>
          {data.summary.map((s) => (
            <Descriptions.Item key={s.label} label={s.label}>
              {s.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : null}
      {data.rows?.length ? (
        <Table
          size="small"
          rowKey={(r) => String(r.key ?? JSON.stringify(r))}
          pagination={false}
          scroll={{ x: "max-content", y: 320 }}
          dataSource={data.rows}
          rowClassName={(r) => (r.tone === "warning" ? "wms-row-warning" : "")}
          columns={data.columns.map((c) => ({ title: c.label, dataIndex: c.key, align: c.align ?? "left" }))}
        />
      ) : null}
    </div>
  );
}
